import type {
  EFormPrintLayout,
  EFormPrintLayoutDocument,
  EFormPrintOutputMode,
  EFormPrintTemplate,
} from './useData';

export const DEFAULT_PRINT_MARGINS = { top: 25.4, right: 25.4, bottom: 25.4, left: 25.4 };

export const EMPTY_PRINT_LAYOUT: EFormPrintLayout = {
  outputMode: 'none',
  pageSize: 'A4',
  orientation: 'portrait',
  margins: DEFAULT_PRINT_MARGINS,
  title: '',
  header: '',
  footer: '',
  footerSlots: { left: 'none', center: 'page', right: 'datetime' },
  blocks: [],
};

function isPrintDocument(value: unknown): value is EFormPrintLayoutDocument {
  return Boolean(
    value &&
      typeof value === 'object' &&
      Array.isArray((value as EFormPrintLayoutDocument).templates),
  );
}

export function asPrintDocument(
  json: unknown,
  fallbackSource = false,
): EFormPrintLayoutDocument {
  if (isPrintDocument(json) && json.templates.length) {
    const templates = json.templates.map((template, index) =>
      normalizeTemplate(template, index, fallbackSource),
    );
    const defaultTemplateId = templates.some((template) => template.id === json.defaultTemplateId)
      ? json.defaultTemplateId
      : templates[0].id;
    return { schemaVersion: 1, defaultTemplateId, templates };
  }

  const legacy =
    json && typeof json === 'object' && !Array.isArray(json)
      ? (json as Partial<EFormPrintLayout>)
      : null;
  return {
    schemaVersion: 1,
    defaultTemplateId: 'default',
    templates: [normalizeTemplate({ id: 'default', name: 'Default', ...(legacy ?? {}) }, 0, fallbackSource)],
  };
}

export function normalizeTemplate(
  template: Partial<EFormPrintTemplate> & { id?: string; name?: string },
  index = 0,
  fallbackSource = false,
): EFormPrintTemplate {
  const outputMode =
    template.outputMode ??
    (template.blocks?.length ? 'designed' : fallbackSource ? 'source' : 'none');
  return {
    ...EMPTY_PRINT_LAYOUT,
    ...template,
    id: template.id || `template-${index + 1}`,
    name: template.name?.trim() || (index === 0 ? 'Default' : `Printout ${index + 1}`),
    outputMode,
    pageSize: template.pageSize ?? 'A4',
    orientation: template.orientation ?? 'portrait',
    margins: {
      ...DEFAULT_PRINT_MARGINS,
      ...(template.margins ?? {}),
    },
    footerSlots: {
      left: 'none',
      center: 'page',
      right: 'datetime',
      ...(template.footerSlots ?? {}),
    },
    blocks: template.blocks ?? [],
  };
}

export function pickPrintTemplate(
  json: unknown,
  templateId?: string | null,
  fallbackSource = false,
): EFormPrintTemplate {
  const document = asPrintDocument(json, fallbackSource);
  return (
    document.templates.find((template) => template.id === templateId) ??
    document.templates.find((template) => template.id === document.defaultTemplateId) ??
    document.templates[0]
  );
}

export function listPrintTemplates(
  json: unknown,
  mode?: EFormPrintOutputMode | 'designed-or-legacy',
): EFormPrintTemplate[] {
  const document = asPrintDocument(json);
  if (!mode) return document.templates;
  if (mode === 'designed-or-legacy') {
    return document.templates.filter(
      (template) =>
        template.outputMode === 'designed' ||
        (!template.outputMode && Boolean(template.blocks?.length)),
    );
  }
  return document.templates.filter((template) => template.outputMode === mode);
}

export function createPrintTemplate(
  existing: EFormPrintTemplate[],
  patch: Partial<EFormPrintTemplate> = {},
): EFormPrintTemplate {
  const index = existing.length + 1;
  return normalizeTemplate(
    {
      ...EMPTY_PRINT_LAYOUT,
      id: `printout-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      name: `Printout ${index}`,
      ...patch,
    },
    index - 1,
  );
}

export function printModeLabel(mode?: EFormPrintOutputMode): string {
  if (mode === 'table') return 'Table';
  if (mode === 'designed') return 'Single record';
  if (mode === 'source') return 'Source template';
  if (mode === 'none') return 'No print';
  return 'Unset';
}

export function serializePrintDocument(document: EFormPrintLayoutDocument): EFormPrintLayoutDocument {
  return {
    schemaVersion: 1,
    defaultTemplateId: document.defaultTemplateId,
    templates: document.templates.map((template, index) => normalizeTemplate(template, index)),
  };
}
